import React, { useEffect, useRef, useState } from "react";
import { Keyboard, TextInput } from "react-native";
import { MainButton, MainText, PressableOpacity } from "app/components/common";
import { updateTeamRequest } from "@microdoing/shared/services/team/actions";
import Modal from "react-native-modal";
import i18n from "@microdoing/shared/i18n";
import { widthPercentageToDP as wp } from "react-native-responsive-screen";
import {
  getUserFirstName,
  getUserId,
} from "@microdoing/shared/services/user/selectors";
import theme from "app/Theme/Theme";
import {
  isAndroid,
  isIos,
  isWebDesktop,
} from "@microdoing/shared/utils/deviceDetectors";
import { useDispatch, useSelector } from "react-redux";
import {
  getTeamId,
  getTeamName,
} from "@microdoing/shared/services/userCampaign/selectors";
import { getTeamNameRequesting } from "@microdoing/shared/services/team/selectors";
import { Box, MainTitle } from "app/components/common";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import CrossIcon from "app/components/common/Icons/CrossIcon";
import normalize from "app/utils/normalize";
import Animated, {
  useAnimatedKeyboard,
  useAnimatedStyle,
} from "react-native-reanimated";
import RefreshIcon from "app/components/Icons/RefrreshIcon";
import treeList from "app/utils/treeList";

const MAX_WIDTH = isWebDesktop ? 450 : undefined;

const TeamSettingsModal = ({ isVisible, triggerTeamModal }) => {
  let isMounted = useRef(false);
  const inputRef = useRef(null);

  const userId = useSelector(getUserId);
  const firstName = useSelector(getUserFirstName);
  const sagaTeamName = useSelector(getTeamName);
  const teamId = useSelector(getTeamId);

  const [teamName, setTeamName] = useState(sagaTeamName);
  const requesting = useSelector(getTeamNameRequesting);

  const dispatch = useDispatch();

  const onChangeName = newTeamName => {
    setTeamName(newTeamName);
  };

  const submitChange = () => {
    dispatch(
      updateTeamRequest({
        teamId,
        firstName: firstName,
        name: teamName,
        userId: userId,
      }),
    );
  };

  useEffect(() => {
    if (!requesting && !!isMounted?.current) {
      triggerTeamModal();
    }

    if (!isMounted?.current) {
      isMounted.current = true;
    }

    () => {
      isMounted.current = false;
    };
  }, [requesting]);

  const insets = useSafeAreaInsets();

  const keyboard = useAnimatedKeyboard();
  const translateStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: isIos ? -keyboard.height.value : 0 }],
    };
  });

  const generateRandomTeamName = () => {
    setTeamName(treeList[Math.floor(Math.random() * treeList.length)]);
  };

  return (
    <Modal
      style={{
        margin: 0,
      }}
      onModalShow={() => {
        if (isAndroid) {
          setTimeout(() => {
            inputRef?.current?.blur();
            inputRef?.current?.focus();
          }, 100);
        }
      }}
      isVisible={isVisible}
      useNativeDriver
      onBackdropPress={triggerTeamModal}
      hideModalContentWhileAnimating={true}
      backdropTransitionOutTiming={0}
    >
      <Box
        position="relative"
        backgroundColor="neutral.white"
        padding={isWebDesktop ? "l" : "sm"}
        paddingHorizontal="xm"
        alignSelf={isWebDesktop ? "center" : undefined}
        borderRadius={isWebDesktop ? 8 : 0}
        style={{ paddingTop: insets.top ? insets.top : theme.spacing.s }}
        height={!isWebDesktop ? "100%" : "auto"}
        width={MAX_WIDTH}
      >
        <PressableOpacity
          onPress={triggerTeamModal}
          style={{
            zIndex: 100,
            position: "absolute",
            top: isWebDesktop
              ? theme.spacing.xm
              : insets.top
              ? insets.top + theme.spacing.s
              : theme.spacing.s,
            right: theme.spacing.xm,
            borderRadius: normalize(8),
            backgroundColor: theme.colors["brand.50"],
            justifyContent: "center",
            alignItems: "center",
            padding: theme.spacing.xxs,
          }}
        >
          <CrossIcon
            width={normalize(16)}
            height={normalize(16)}
            color={theme.colors["brand.500"]}
          />
        </PressableOpacity>
        <MainTitle
          textAlign="left"
          fontWeight="bold"
          color="neutral.900"
          marginTop="s"
          variant="l8-bold"
        >
          {i18n.t("modify-team")}
        </MainTitle>
        <MainText marginTop="sm" variant="l3-medium" color="neutral.600">
          {i18n.t("choose-team-name")}
        </MainText>
        <Box minHeight={isWebDesktop ? 40 : undefined} flex={1} />

        <Animated.View style={translateStyle}>
          <TextInput
            autoFocus={isIos}
            ref={inputRef}
            onChangeText={onChangeName}
            style={{ ...theme.body["l8-medium"], outline: "none" }}
            value={teamName}
            placeholder={i18n.t("team-name")}
            multiline={true}
          />
          <PressableOpacity
            onPress={generateRandomTeamName}
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginTop: theme.spacing.sm,
            }}
          >
            <MainText variant="l3-medium" color="brand.500" marginRight="xxs">
              {i18n.t("generate-random-team-name")}
            </MainText>
            <RefreshIcon
              color={theme.colors["brand.500"]}
              width={normalize(16)}
              height={normalize(16)}
            />
          </PressableOpacity>
          <MainButton
            marginBottom="null"
            marginTop="xm"
            text={i18n.t("save-team-name")}
            onPress={submitChange}
            isLoading={requesting}
          />
        </Animated.View>
      </Box>
    </Modal>
  );
};

export default TeamSettingsModal;
